// Real API service for vehicles
import { getApiBaseUrl } from '@/config/environment';


export interface VehicleAPIResponse {
  vehicles: any[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface DashboardStatsResponse {
  totalVehicles: number;
  activeVehicles: number;
  rentedVehicles: number;
  pendingVehicles: number;
  revenue: number;
  utilizationRate: number;
}

class VehicleAPIService {
  private async request(endpoint: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('token');
    const response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    });
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }
    return response.json();
  }

  // Backend API for mobile host vehicle endpoints
  private async backendRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('token');
    
  const response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`Backend API Error: ${response.status}`);
    }

    return response.json();
  }

  // Get approved vehicles for agent-assisted booking from admin approvals endpoint
  async getVehicles(params: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    search?: string;
    minPrice?: number;
    maxPrice?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  } = {}): Promise<VehicleAPIResponse> {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const endpoint = `/api/vehicles${queryString ? `?${queryString}` : ''}`;


    const response = await this.request(endpoint);

    // Backend now returns { success: true, data: [...], pagination: {...} }
    // Transform to match our expected format { vehicles: [...], pagination: {...} }
    if (response.success && response.data) {
      return {
        vehicles: response.data,
        pagination: response.pagination || {
          page: params.page || 1,
          limit: params.limit || 100,
          total: response.data.length,
          totalPages: 1
        }
      };
    }

    // Fallback for old format
    return response;
  }

  async getVehicleById(id: string): Promise<any> {
    return this.request(`/api/vehicles/${id}`);
  }

  async createVehicle(vehicleData: any): Promise<any> {
    // In production, simulate successful creation since backend is not deployed
    if (import.meta.env.MODE === 'production') {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay
      return {
        id: Date.now().toString(),
        ...vehicleData,
        status: 'active',
        createdAt: new Date().toISOString()
      };
    }
    
    return this.request('/api/vehicles', {
      method: 'POST',
      body: JSON.stringify(vehicleData),
    });
  }

  async updateVehicle(id: string, updates: any): Promise<any> {
    // In production, simulate successful update since backend is not deployed
    if (import.meta.env.MODE === 'production') {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay
      return {
        id,
        ...updates,
        updatedAt: new Date().toISOString()
      };
    }
    
    return this.request(`/api/vehicles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteVehicle(id: string): Promise<any> {
    return this.request(`/api/vehicles/${id}`, {
      method: 'DELETE',
    });
  }

  async updateVehicleStatus(id: string, status: string, location?: { latitude: number; longitude: number }): Promise<any> {
    return this.request(`/api/vehicles/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, ...location }),
    });
  }

  async updateVerificationScores(id: string, scores: { verificationScore?: number; qualityScore?: number }): Promise<any> {
    return this.request(`/api/vehicles/${id}/verification`, {
      method: 'PUT',
      body: JSON.stringify(scores),
    });
  }

  async updateInspection(id: string, inspection: { lastInspection?: string; nextInspectionDue?: string }): Promise<any> {
    return this.request(`/api/vehicles/${id}/inspection`, {
      method: 'PUT',
      body: JSON.stringify(inspection),
    });
  }

  async getDashboardStats(): Promise<DashboardStatsResponse> {
    return this.request('/api/vehicles/dashboard/stats');
  }

  // Get approved vehicles for agent-assisted booking from admin approvals endpoint
  async getApprovedVehiclesFromApprovals(params: {
    limit?: number;
    offset?: number;
    type?: string;
    search?: string;
    minPrice?: number;
    maxPrice?: number;
  } = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    
    // Note: Using admin approvals endpoint which returns driver data
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && (key === 'limit' || key === 'offset')) {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/admin/approvals${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.request(endpoint);
    
    // Transform driver data to vehicle-like format for agent-assisted booking
    const drivers = response?.data?.approvals || [];
    const vehicles = drivers.map((driver: any) => ({
      id: driver.id,
      vehicle_id: driver.driverId,
      make: 'Driver Service',
      model: driver.applicant?.name || 'Professional Driver',
      year: new Date().getFullYear(),
      color: 'N/A',
      plate_number: driver.driverId || 'N/A',
      transmission: 'N/A',
      fuel_type: 'N/A',
      seating_capacity: 4,
      pricePerDay: driver.driverDetails?.dailyRate || 20000,
      dailyRate: driver.driverDetails?.dailyRate || 20000,
      hourlyRate: driver.driverDetails?.hourlyRate || 2500,
      status: 'approved',
      instant_booking: true,
      host: {
        id: driver.applicant?.id,
        name: driver.applicant?.name || 'Driver',
        email: driver.applicant?.email,
        phone: driver.applicant?.phone
      },
      photos: {
        count: driver.documents?.filter((doc: any) => doc.hasPhoto).length || 0,
        items: []
      },
      createdAt: driver.createdAt,
      updatedAt: driver.createdAt
    }));
    
    return {
      vehicles: vehicles,
      pagination: response?.data?.pagination
    };
  }

  async getAvailableVehicles(params: {
    startDate?: string;
    endDate?: string;
    location?: string;
  } = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });
    const queryString = queryParams.toString();
    const endpoint = `/api/mobile/vehicles${queryString ? `?${queryString}` : ''}`;
    const res = await this.request(endpoint);
    // The backend returns { success, data: { vehicles, ... } }
    const vehicles = res?.data?.vehicles || [];
    return { vehicles };
  }

  // Host Vehicle Management (used by mobile app)
  async getHostVehicles(params: {
    hostId?: string;
    vehicleType?: 'car' | 'yacht' | 'boat';
    status?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const endpoint = `/api/mobile/host/vehicles${queryString ? `?${queryString}` : ''}`;
    
    return this.backendRequest(endpoint);
  }

  async getHostCars(hostId?: string): Promise<any> {
    return this.getHostVehicles({ 
      hostId,
      vehicleType: 'car' 
    });
  }

  async getHostYachts(hostId?: string): Promise<any> {
    return this.getHostVehicles({ 
      hostId,
      vehicleType: 'yacht' 
    });
  }

  async getHostBoats(hostId?: string): Promise<any> {
    return this.getHostVehicles({
      hostId,
      vehicleType: 'boat'
    });
  }

  // Get admin vehicles (includes type and body type fields)
  // Uses /api/v1/admin/vehicles/pending endpoint with optimized backend filters
  async getAdminVehicles(params: {
    status?: string; // 'all', 'pending_approval', 'approved', 'rejected', 'draft'
    vehicle_type?: string; // 'all', 'sedan', 'suv', 'truck', 'van', etc.
    fuel_type?: string; // 'all', 'petrol', 'diesel', 'electric', 'hybrid'
    transmission?: string; // 'all', 'automatic', 'manual'
    limit?: number;
    offset?: number;
    search?: string;
  } = {}): Promise<VehicleAPIResponse> {
    const queryParams = new URLSearchParams();

    // Default to 'approved' to show only approved vehicles for booking
    const status = params.status || 'approved';

    // Build query params
    const apiParams = { ...params, status };

    // Convert page to offset if page is provided
    if ('page' in apiParams) {
      const page = (apiParams as any).page;
      const limit = apiParams.limit || 100;
      apiParams.offset = (page - 1) * limit;
      delete (apiParams as any).page;
    }

    Object.entries(apiParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== 'all') {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/admin/vehicles/pending${queryString ? `?${queryString}` : ''}`;


    const response = await this.request(endpoint);

    // Transform response to match expected format
    if (response.success && response.data) {
      return {
        vehicles: response.data.vehicles || response.data,
        pagination: response.data.pagination || response.pagination || {
          page: Math.floor((params.offset || 0) / (params.limit || 100)) + 1,
          limit: params.limit || 100,
          total: (response.data.vehicles || response.data).length,
          totalPages: 1
        }
      };
    }

    // Fallback for old format
    return response;
  }

  // Get vehicles with airport pricing (for airport transfer bookings)
  async getVehiclesWithAirportPricing(params: {
    airportId?: string;
    zoneId?: string;
    hasPricing?: boolean;
    includePricingData?: boolean;
    page?: number;
    limit?: number;
  } = {}): Promise<any> {
    const queryParams = new URLSearchParams();

    if (params.airportId) queryParams.append('airportId', params.airportId);
    if (params.zoneId) queryParams.append('zoneId', params.zoneId);
    if (params.hasPricing !== undefined) queryParams.append('hasPricing', params.hasPricing.toString());
    if (params.includePricingData !== undefined) queryParams.append('includePricingData', params.includePricingData.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/admin/vehicles/airport-pricing${queryString ? `?${queryString}` : ''}`;


    const response = await this.request(endpoint);

    // Backend returns { success: true, data: { vehicles: [...], pagination: {...} } }
    if (response.success && response.data) {
      return {
        vehicles: response.data.vehicles || [],
        pagination: response.data.pagination || {
          page: params.page || 1,
          limit: params.limit || 50,
          total: response.data.vehicles?.length || 0,
          totalPages: 1
        }
      };
    }

    // Fallback
    return { vehicles: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 0 } };
  }
}

export const vehicleAPI = new VehicleAPIService();